import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './addProjectModal.css';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../utils/axiosInstance';



const AddProjectModal = ({ adminIds, empRole, loggedInEmployeeId, onClose, onSave, project }) => {

  const { t } = useTranslation();

  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [start_date, setStartDate] = useState('');
  const [end_date, setEndDate] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [descriptionError, setDescriptionError] = useState('');
  const [projectNameError, setProjectNameError] = useState('');
  const [modalVisible, setModalVisible] = useState(true); // State to manage modal visibility
  const [formValid, setFormValid] = useState(true); // State to manage form validity

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axiosInstance.get('/admin/employees');
        const allEmployees = response.data;
        const employeesToShow = empRole === 'SuperAdmin'
          ? allEmployees
          : allEmployees.filter(emp => adminIds.includes(emp.emp_id));

        const formattedEmployees = employeesToShow.map(emp => ({
          ...emp,
          fullName: emp.p_name,
        }));
        setFilteredEmployees(formattedEmployees);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };

    fetchEmployees();

    if (project) {
      setProjectName(project.name);
      setDescription(project.description);
      setStartDate(project.startDate);
      setEndDate(project.endDate);
      setSelectedEmployees(project.employees.map(emp => ({
        emp_id: emp.id,
        fullName: emp.name
      })));
    }
  }, [adminIds, project]);

  useEffect(() => {
    // Check if end date is before start date
    if (start_date && end_date) {
      const startDateObj = new Date(start_date);
      const endDateObj = new Date(end_date);
      setFormValid(startDateObj <= endDateObj);
    }
  }, [start_date, end_date]);

  const handleSave = async () => {
    if (!formValid) {
      alert('End Date cannot precede Start Date');
      return;
    }

    if (!projectName.trim()) {
      alert('Project Name is required');
      return;
    }

    if (projectName.split(' ').length > 5) {
      alert('Project Name exceeds the 5-word limit');
      return;
    }

    if (!start_date || !end_date) {
      alert('Start Date and End Date are required');
      return;
    }

    if (description.split(' ').length > 70) {
      alert('Description exceeds the 70-word limit');
      return;
    }

    const newProject = {
      project_name: projectName,
      project_desc: description,
      start_date: start_date,
      end_date: end_date,
      project_status: 'Working',
      manager_id: loggedInEmployeeId,
      employees: selectedEmployees.map(emp => emp.emp_id),
    };

    if (project) {
      newProject.id = project.id;
    }

    try {
      const response = project
        ? await axiosInstance.put(`/admin/updateProject/${project.id}`, newProject)
        : await axiosInstance.post('/admin/addProjects', newProject);
      const { projectId } = response.data;

      if (!project) {
        newProject.id = projectId;
        await Promise.all(selectedEmployees.map(emp =>
          axiosInstance.post('/admin/addProjectEmp', {
            project_id: projectId,
            emp_id: emp.emp_id,
          })
        ));
      } else {
        await Promise.all(selectedEmployees.map(emp =>
          axiosInstance.post('/admin/addProjectEmp', {
            project_id: project.id,
            emp_id: emp.emp_id,
          })
        ));
      }

      onSave(newProject);
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error adding/updating project:', error);
    }
  };

  const handleEmployeeSelect = (employee) => {
    if (!selectedEmployees.some((e) => e.emp_id === employee.emp_id)) {
      setSelectedEmployees([...selectedEmployees, employee]);
    }
    setEmployeeSearch('');
    setDropdownVisible(false);
  };

  const handleEmployeeDeselect = async (employee) => {
    setSelectedEmployees(selectedEmployees.filter((e) => e !== employee));

    try {
      await axiosInstance.put(`/admin/updateEmpStatus`, {
        project_id: project.id,
        emp_id: employee.emp_id,
        employee_status: 'Inactive'
      });
    } catch (error) {
      console.error('Error updating employee status:', error);
    }
  };

  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    const wordCount = newDescription.split(' ').filter(word => word !== '').length;

    if (wordCount <= 70) {
      setDescription(newDescription);
      setDescriptionError('');
    } else {
      setDescriptionError('Description exceeds the 70-word limit');
    }
  };

  const handleProjectNameChange = (e) => {
    const newProjectName = e.target.value;
    const wordCount = newProjectName.split(' ').filter(word => word !== '').length;

    if (wordCount <= 5) {
      setProjectName(newProjectName);
      setProjectNameError('');
    } else {
      setProjectNameError('Project Name exceeds the 5-word limit');
    }
  };

  return (
    <div className="modalOverlays">
      <div className="modalContents">
        <form className="AddProjectCard">
          <h5 className="modalTitle">{project ? t("addproject_update") : t("addproject_title")}</h5> {/* Dynamic Modal title */}
          <div className='AddProjectCardDiv'>
            <div className='vertical-row'>
              <label className='projectLabel' htmlFor="projectName">{t("addproject_name")}</label>
              <input
                id="projectName"
                className="addProjectInput input"
                type="text"
                value={projectName}
                onChange={handleProjectNameChange}
                placeholder={t("addProject_namePlaceholder")}
              />
              {projectNameError && <p className="error">{projectNameError}</p>}
            </div>
            <div className='vertical-row'>
              <label className='descLabel' htmlFor="description">{t("addProject_desc")}</label>
              <textarea
                id="description"
                className="descBox ReasonTextArea"
                value={description}
                onChange={handleDescriptionChange}
                placeholder={t("addProject_descPlaceholder")}
              />
              {descriptionError && <p className="error">{descriptionError}</p>}
            </div>
            <div className='alignSessions'>
              <div className='column'>
                <div className='vertical-row'>
                  <label htmlFor="start_date" className='projectModal-date-input'>{t("leaveRequest_startDate")}</label>
                  <input
                    id="start_date"
                    className="input"
                    type="date"
                    style={{ fontSize: '12px' }}
                    value={start_date}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
              </div>
              <div className='column'>
                <div className='vertical-row'>
                  <label htmlFor="end_date" className='projectModal-date-input'>{t("leaveRequest_endDate")}</label>
                  <input
                    id="end_date"
                    className="input"
                    style={{ fontSize: '12px' }}
                    type="date"
                    value={end_date}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

            </div>
            {!formValid && <p className="small-text">{t("addProject_datePrecede")}</p>}
            <div className='vertical-row'>
              <label className='employeeSelectionLabel' htmlFor="employeeSearch">{t("addProject_selectEmp")}</label>
              <div className="dropdown">
                <input
                  id="employeeSearch"
                  type="text"
                  className="input employeeSearchInput"
                  placeholder={t("addProject_empPlaceholder")}
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  onFocus={() => setDropdownVisible(true)}
                  onBlur={() => setDropdownVisible(false)}
                />
                {dropdownVisible && (
                  <div className="dropdownContent">
                    {filteredEmployees
                      .filter((employee) => !selectedEmployees.some((e) => e.emp_id === employee.emp_id))
                      .filter((employee) => {
                        const q = employeeSearch.trim().toLowerCase();
                        if (!q) return true;
                        const name = (employee.fullName || '').toLowerCase();
                        const email = (employee.emp_mail || employee.p_mail || '').toLowerCase();
                        const sap = String(employee.emp_sap_id || '').toLowerCase();
                        return name.includes(q) || email.includes(q) || sap.includes(q);
                      })
                      .map((employee) => (
                        <div
                          key={employee.emp_id}
                          className="dropdownItem"
                          onMouseDown={() => handleEmployeeSelect(employee)}
                        >
                          {employee.fullName}
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <div className="selectedEmployees">
                {selectedEmployees.map((employee) => (
                  <div key={employee.emp_id} className="selectedEmployee">
                    {employee.fullName}
                    <span
                      className="removeEmployee"
                      onClick={() => handleEmployeeDeselect(employee)}
                    >
                      &times;
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className='vertical-row'>
              <div className='LeaveCArdButtons'>
                <button className="btn btn-primary" style={{ fontSize: '12px', width: '70px' }} type="button" onClick={handleSave} disabled={!formValid}>{project ? t("addProject_updatebtn") : t("addProject_save")}</button> {/* Dynamic button text */}
                <button className="btn btn-danger" style={{ fontSize: '12px', width: '70px' }} type="button" onClick={onClose}>{t("addProject_cancel")}</button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProjectModal;
